/**
 * Formateo de fechas y horas en la zona del POLICLÍNICO, no en la del navegador.
 *
 * El backend manda instantes en UTC (ISO con offset). `toLocaleString()` a secas
 * los pinta en la zona de quien mira: el administrador conectado desde Madrid
 * veía las citas 6 horas corridas. Todo lo que sea un instante pasa por acá con
 * la `timeZone` del tenant.
 *
 * Aparte están las fechas SIN hora (`fecha_nacimiento`, "1988-03-14"). Esas no
 * son instantes: `new Date("1988-03-14")` se interpreta como medianoche UTC y
 * en Lima (UTC-5) retrocede al día 13. Se formatean como texto, sin Date.
 */

export const ZONA_POR_DEFECTO = "America/Lima";
const LOCALE = "es-PE";

/** Fecha y hora completas: "07/08/2026, 09:00". */
export function fechaHora(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(LOCALE, {
    timeZone: tz,
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Solo la hora: "09:00". */
export function hora(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(LOCALE, { timeZone: tz, timeStyle: "short" });
}

/** Solo el día de un instante: "07/08/2026". */
export function fechaDeInstante(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(LOCALE, { timeZone: tz });
}

/** Día largo: "viernes, 7 de agosto de 2026". */
export function fechaLarga(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(LOCALE, {
    timeZone: tz,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Fecha SIN hora ("1988-03-14") → "14/03/1988".
 *
 * No pasa por Date a propósito: convertirla implicaría inventarle una hora y
 * una zona, y ahí es donde los cumpleaños se corren un día.
 */
export function fechaSimple(valor: string | null | undefined): string {
  if (!valor) return "—";
  const [y, m, d] = valor.slice(0, 10).split("-");
  if (!y || !m || !d) return valor;
  return `${d}/${m}/${y}`;
}

/** "YYYY-MM-DD" del día en que cae un instante, en la zona del policlínico. */
export function diaEnZona(iso: string | null | undefined, tz: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { timeZone: tz });
}

/** "YYYY-MM-DD" del día de HOY en la zona del policlínico. */
export function hoyEnZona(tz: string): string {
  // en-CA da directamente el formato ISO corto.
  return new Date().toLocaleDateString("en-CA", { timeZone: tz });
}

/**
 * "YYYY-MM-DD" + "HH:MM" locales → instante ISO en UTC.
 *
 * Para mandar al backend algo que el usuario escribió como hora de pared.
 * Se resuelve midiendo el offset real de la zona en esa fecha, así funciona
 * también donde hay horario de verano.
 */
export function localAIso(fecha: string, horaHHMM: string, tz: string): string {
  const tentativo = new Date(`${fecha}T${horaHHMM}:00Z`);
  const offsetMin = offsetDeZona(tentativo, tz);
  return new Date(tentativo.getTime() - offsetMin * 60_000).toISOString();
}

/** Offset de la zona en minutos para un instante dado (Lima → -300). */
function offsetDeZona(instante: Date, tz: string): number {
  const enZona = new Date(instante.toLocaleString("en-US", { timeZone: tz }));
  const enUtc = new Date(instante.toLocaleString("en-US", { timeZone: "UTC" }));
  return (enZona.getTime() - enUtc.getTime()) / 60_000;
}
